import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ADMIN_SIDEBAR_ITEMS,
  AGENT_SIDEBAR_ITEMS,
  BUILDER_SIDEBAR_ITEMS,
  LOCAL_STORAGE_KEYS,
  ROLES,
  SALES_SIDEBAR_ITEMS,
} from "../../constants";
import SvgIcon from "../../assets/icons/SvgIcon";
import "./index.scss";
import { SidebarItem } from "../../types";
import { filterSidebarItems, getLocalStorage } from "../../utils";
import {
  ADMIN_ROUTES,
  AGENT_ROUTES,
  BUILDER_ROUTES,
  SALES_ROUTES,
} from "../../constants/appRoutes";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/reducers/allReducers";
import Image from "../Image";
import { AWS_BASE_URL } from "../../clientConfig";
import UserImage from "../../assets/images/user.png";

interface IProps {
  sidebarOpen: boolean;
  closeSidebar: () => void;
}

const AdminSidebar: React.FC<IProps> = ({ sidebarOpen, closeSidebar }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [openMenus, setOpenMenus] = useState<any>({});
  const userRole = getLocalStorage(LOCAL_STORAGE_KEYS.USER_ROLE);
  const { userInfo }: any = useSelector((state: RootState) => state.user);

  const handleProfile = () => {
    if (userRole === ROLES.ADMIN || userRole === ROLES.STAFF) {
      navigate(`${ADMIN_ROUTES.ROOT}/${ADMIN_ROUTES.PROFILE}`);
    } else if (userRole === ROLES.AGENT) {
      navigate(`${AGENT_ROUTES.ROOT}/${AGENT_ROUTES.PROFILE}`);
    } else if (userRole === ROLES.BUILDER) {
      navigate(`${BUILDER_ROUTES.ROOT}/${BUILDER_ROUTES.PROFILE}`);
    } else if (userRole === ROLES.SALES) {
      navigate(`${SALES_ROUTES.ROOT}/${SALES_ROUTES.PROFILE}`);
    }
  };

  const [filteredSidebarItems, setFilteredSidebarItems] = useState<
    SidebarItem[]
  >([]);

  const toggleMenu = (path: string) => {
    setOpenMenus((prev: any) => ({ ...prev, [path]: !prev[path] }));
  };

  const isRouteAllowed = (items: any[], pathname: string): boolean => {
    for (const item of items) {
      if (item.url === pathname) {
        // Direct match for the current path
        return true;
      }
      if (item.children) {
        // Check the children recursively
        if (isRouteAllowed(item.children, pathname)) {
          return true;
        }
      }
    }
    return false;
  };

  useEffect(() => {
    const openMenuPaths: any = {};

    const setInitialOpenMenus = (items: any, parentPath = "") => {
      items.forEach((item: any) => {
        const currentPath: string = parentPath
          ? `${parentPath} > ${item.name}`
          : item.name;
        if (item.children) {
          item.children.forEach((child: any) => {
            if (location.pathname.includes(child.url)) {
              openMenuPaths[currentPath] = true;
            }
          });
          setInitialOpenMenus(item.children, currentPath);
        }
      });
    };
    let filteredItems;
    if (userInfo && userRole === ROLES.STAFF) {
      filteredItems = filterSidebarItems(
        ADMIN_SIDEBAR_ITEMS,
        userInfo?.permissions
      );

      // Check if the current route or parent is allowed
      const routeAllowed = isRouteAllowed(filteredItems, location.pathname);

      // If the current route is not allowed, redirect to /admin
      if (!routeAllowed) {
        navigate(ADMIN_ROUTES.ROOT);
      }
    }
    setFilteredSidebarItems(filteredItems ?? ADMIN_SIDEBAR_ITEMS);

    setInitialOpenMenus(filteredItems ?? ADMIN_SIDEBAR_ITEMS);
    setOpenMenus(openMenuPaths);
  }, [userInfo?.permissions]);

  const handleCheckCurrentRoute = (menuKey: string, url: string): boolean => {
    if (menuKey) {
      const currentRouter = location.pathname.split("/");
      if (menuKey === "main") {
        return location.pathname === url;
      } else {
        const checkKey = currentRouter.some((item) =>
          item.toLowerCase().includes(menuKey.toLowerCase())
        );
        return checkKey;
      }
    }
    return false;
  };

  const renderMenu = (items: any, parentPath = "") => {
    return items.map((item: any) => {
      const currentPath: string = parentPath
        ? `${parentPath} > ${item.name}`
        : item.name;
      const isOpen = openMenus[currentPath];

      return (
        <li key={currentPath} className="mb-2">
          {item.children ? (
            <>
              <button
                className="w-full text-left py-2 px-4 hover:bg-deep-teal hover:text-white flex gap-2 items-center"
                onClick={() => {
                  toggleMenu(currentPath);
                  closeSidebar();
                }}
              >
                {/* <Tooltip
                  message={item.name}
                  showTooltip={!sidebarOpen}
                  position="right"
                > */}
                <SvgIcon
                  iconName={item.icon}
                  className={`menu-icon ${item?.className} new-menu-icon`}
                />
                {/* </Tooltip> */}
                <div className="flex w-full justify-between items-center">
                  <span>{item.name}</span>
                  <span>
                    {isOpen ? (
                      <SvgIcon iconName="arrow-up" className="arrow" />
                    ) : (
                      <SvgIcon iconName="arrow-down" className="arrow" />
                    )}
                  </span>
                </div>
              </button>
              {isOpen && (
                <ul className="pl-4">
                  {renderMenu(item.children, currentPath)}
                </ul>
              )}
            </>
          ) : (
            <Link
              to={item.url}
              className={`flex gap-2 items-center py-2 px-4 hover:bg-deep-teal rounded hover:text-white ${
                handleCheckCurrentRoute(item.menuKey, item.url)
                  ? `active text-white bg-[#89A9CD]`
                  : ""
              }`}
              onClick={closeSidebar}
            >
              {/* <Tooltip
                message={item.name}
                showTooltip={!sidebarOpen}
                position="right"
              > */}
              <SvgIcon
                iconName={item.icon}
                className={`menu-icon ${item?.className} [&>path]:stroke-white`}
              />
              {/* </Tooltip> */}
              <span>{item.name}</span>
            </Link>
          )}
        </li>
      );
    });
  };

  const handleHomeRouter = (): string => {
    let redirectRouter;
    if (userRole === ROLES.SALES) redirectRouter = SALES_ROUTES.ROOT;
    else if (userRole === ROLES.AGENT) redirectRouter = AGENT_ROUTES.ROOT;
    else if (userRole === ROLES.BUILDER) redirectRouter = BUILDER_ROUTES.ROOT;
    else redirectRouter = ADMIN_ROUTES.ROOT;

    return redirectRouter;
  };

  const handleRenderMenus = () => {
    let renderMenu;
    if (userRole === ROLES.ADMIN) renderMenu = ADMIN_SIDEBAR_ITEMS;
    else if (userRole === ROLES.STAFF) renderMenu = filteredSidebarItems;
    else if (userRole === ROLES.SALES) renderMenu = SALES_SIDEBAR_ITEMS;
    else if (userRole === ROLES.BUILDER) renderMenu = BUILDER_SIDEBAR_ITEMS;
    else if (userRole === ROLES.AGENT) renderMenu = AGENT_SIDEBAR_ITEMS;

    return renderMenu;
  };

  return (
    <div
      className={`new-sidebar-section h-dvh fixed bg-Light-Color/10 text-white ${sidebarOpen ? "left-0" : "-left-full"} transition-all ease-in-out duration-200 w-full small:w-72 md:left-0 border-r z-[1000]`}
    >
      <div className="flex justify-between md:justify-center bg-white px-6 items-center border-b py-4 border-b-Light-Color/40">
        <Link to={handleHomeRouter()} onClick={closeSidebar}>
          <SvgIcon
            iconName="savitar-realty-logo"
            className="w-40 h-auto max-h-16"
          />
        </Link>
        <button type="button" className="md:hidden" onClick={closeSidebar}>
          <SvgIcon iconName="close" className="size-8" />
        </button>
      </div>
      <div className="space-y-4 px-3 py-6 overflow-y-auto h-[calc(100vh-68px)] bg-white">
        <div className="flex flex-col justify-between items-start py-4 px-2 bg-steel-blue rounded min-h-full h-fit">
          <div className="w-full">
            <button
              type="button"
              className="group user text-center my-8 w-full"
              onClick={() => {
                handleProfile();
                closeSidebar();
              }}
            >
              <Image
                src={`${AWS_BASE_URL}${userInfo?.profile}`}
                data-src={`${AWS_BASE_URL}${userInfo?.profile}`}
                alt={"User Image"}
                imgClassName="object-cover rounded-full w-[100px] !h-[100px]"
                handleOnError={(e: any) => (e.target.src = UserImage)}
              />
              <h2 className="font-bold text-xl group-hover:text-Blue-Lagoon max-w-96 mx-auto line-clamp-1">
                {userInfo?.full_name}
              </h2>
            </button>
            <ul>{renderMenu(handleRenderMenus())}</ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSidebar;
